import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ShieldCheck, CreditCard, Coins, Wallet, Check, Loader2, Copy, ExternalLink } from 'lucide-react';
import GlassBoxBadge from './GlassBoxBadge';

const presetAmounts = [25, 50, 100, 250];
const paymentMethods = [
  { id: 'card', label: 'Card', icon: CreditCard },
  { id: 'humc', label: 'HUMC Token', icon: Coins },
  { id: 'crypto', label: 'Crypto Wallet', icon: Wallet },
];

function generateFakeTxHash() {
  const chars = '0123456789abcdef';
  let hash = '0x';
  for (let i = 0; i < 64; i++) hash += chars[Math.floor(Math.random() * 16)];
  return hash;
}

export function DonationModal({ open, onOpenChange, projectTitle, ngoName }) {
  const [step, setStep] = useState('amount');
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [method, setMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [txHash] = useState(generateFakeTxHash());
  const [copied, setCopied] = useState(false);

  const selectedAmount = customAmount ? parseInt(customAmount) : amount;

  const handleDonate = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setStep('success');
    }, 2000);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => { setStep('amount'); setCustomAmount(''); setAmount(50); setMethod('card'); }, 300);
  };

  const copyHash = () => {
    navigator.clipboard.writeText(txHash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden border-0" data-testid="donation-modal">
        <div className="bg-gradient-to-r from-[#7C3AED] to-[#2563EB] p-6 pb-5">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">
              {step === 'success' ? 'Donation Verified' : 'Support This Project'}
            </DialogTitle>
            <DialogDescription className="text-white/70 font-body text-sm">
              {step === 'success' ? 'Your impact is now on-chain' : `${projectTitle} by ${ngoName}`}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6">
          {step === 'amount' && (
            <div className="space-y-6" data-testid="donation-amount-step">
              <div>
                <label className="text-sm font-medium text-[#111827] mb-3 block font-heading">Select Amount</label>
                <div className="grid grid-cols-4 gap-2">
                  {presetAmounts.map(a => (
                    <button
                      key={a}
                      onClick={() => { setAmount(a); setCustomAmount(''); }}
                      className={`py-3 rounded-xl text-sm font-semibold transition-all font-heading ${
                        amount === a && !customAmount
                          ? 'bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white shadow-lg'
                          : 'bg-gray-50 text-[#111827] hover:bg-gray-100'
                      }`}
                      data-testid={`amount-${a}`}
                    >
                      ${a}
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  <Input
                    type="number"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="rounded-xl border-gray-200 font-body"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#111827] mb-3 block font-heading">Payment Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {paymentMethods.map(pm => (
                    <button
                      key={pm.id}
                      onClick={() => setMethod(pm.id)}
                      className={`py-3 px-2 rounded-xl text-xs font-medium transition-all flex flex-col items-center gap-2 font-body ${
                        method === pm.id
                          ? 'bg-[#7C3AED]/10 text-[#7C3AED] border-2 border-[#7C3AED]/30'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                      data-testid={`payment-${pm.id}`}
                    >
                      <pm.icon className="w-5 h-5" />
                      {pm.label}
                    </button>
                  ))}
                </div>
              </div>

              {method === 'card' && (
                <div className="space-y-3" data-testid="card-fields">
                  <Input placeholder="Card Number" className="rounded-xl border-gray-200 font-mono font-body" data-testid="card-number" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="MM / YY" className="rounded-xl border-gray-200 font-mono font-body" data-testid="card-expiry" />
                    <Input placeholder="CVC" className="rounded-xl border-gray-200 font-mono font-body" data-testid="card-cvc" />
                  </div>
                </div>
              )}
              {method === 'humc' && (
                <div className="bg-[#10B981]/5 rounded-xl p-4 text-center" data-testid="humc-connect">
                  <Coins className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                  <Button variant="outline" className="rounded-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 font-body">
                    Connect HumaCoin Wallet
                  </Button>
                </div>
              )}
              {method === 'crypto' && (
                <div className="bg-[#2563EB]/5 rounded-xl p-4 text-center" data-testid="crypto-connect">
                  <Wallet className="w-8 h-8 text-[#2563EB] mx-auto mb-2" />
                  <Button variant="outline" className="rounded-full border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10 font-body">
                    Connect MetaMask
                  </Button>
                </div>
              )}

              <Button
                onClick={handleDonate}
                disabled={processing || !selectedAmount || selectedAmount <= 0}
                className="w-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full py-3 text-base font-medium hover:shadow-lg transition-all border-0 h-auto"
                data-testid="donate-submit-btn"
              >
                {processing ? (
                  <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Processing...</span>
                ) : (
                  `Donate $${selectedAmount || 0}`
                )}
              </Button>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center space-y-5" data-testid="donation-success">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto">
                <Check className="w-8 h-8 text-[#10B981]" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl text-[#111827] mb-1">
                  Thank you! Your donation is verified on-chain.
                </h3>
                <p className="text-sm text-gray-500 font-body">
                  ${selectedAmount?.toLocaleString()} donated to {projectTitle}
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <GlassBoxBadge label="On-chain" txHash={txHash} />
                </div>
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                  <code className="text-xs text-gray-600 truncate flex-1">{txHash.slice(0, 20)}...{txHash.slice(-8)}</code>
                  <button onClick={copyHash} className="ml-2 text-gray-400 hover:text-[#7C3AED] transition-colors" data-testid="copy-tx-hash">
                    {copied ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <a
                  href={`https://bscscan.com/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-1.5 text-sm text-[#7C3AED] hover:text-[#2563EB] font-medium transition-colors font-body"
                  data-testid="view-receipt-link"
                >
                  View your receipt on BscScan <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <p className="text-xs text-gray-400 font-body">View your impact dashboard (coming soon)</p>
              </div>

              <div className="flex justify-center gap-2 pt-2">
                {['Twitter', 'Facebook', 'LinkedIn'].map(platform => (
                  <button
                    key={platform}
                    className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors font-body"
                    data-testid={`share-${platform.toLowerCase()}`}
                  >
                    {platform}
                  </button>
                ))}
              </div>

              <Button onClick={handleClose} variant="outline" className="w-full rounded-full mt-2" data-testid="donation-close-btn">
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DonationModal;
